import React, { FC, useCallback, useEffect, useState } from 'react';

import {
  useAddInspectionsVehicleDamageActions,
  useDeleteInspectionsVehicleDamageAction,
  useInspectionSearchLabour,
} from '@/api/Inspections/hooks/useInspectionsVehicles';
import { useTranslate } from '@/translations';

import {
  Alert,
  App,
  Button,
  Card,
  InputNumber,
  Select,
  Spin,
  Table,
} from 'antd';
import { Damage, DamageAction, DamageActionData, LabourCode } from '@/types';
import {
  faAdd,
  faEdit,
  faMinus,
  faTrash,
  faXmark,
  Icon,
} from '@/ui/components';
import { ColumnsType } from 'antd/es/table';
import { StyledLink } from '@/ui/globalStyles';
import { currencyFormatted } from '@/utils/helpers';
import { DamageSupplierPartsGrid } from '../DamageParts';
import { getActionParts } from './utils';
import { getDamageLineOptions } from './DamageLineOptions';
import { useRefreshDamageLines } from '../useRefreshDamageLines';
import { addFrShortcutHint, isAddFrShortcut } from '../preTimingShortcuts';
import { useInlineAddShortcut } from '../useInlineAddShortcut';
import {
  isExistingRowEditing,
  patchEditingRow,
  removeEditingRow,
  upsertEditingRow,
  withEditingRows,
} from '../inlineRowEdit';

interface DamageLabourGridProps {
  title?: string;
  canEditFr?: boolean;
  canEditParts?: boolean;
  inspectionId: number;
  damageLine?: {
    damageLineId: number;
    dataSource: DamageAction[];
  };
  damageLines?: Damage[];
  preferredDamageLineIds?: number[];
  damageLinesLoading?: boolean;
  onSubmit: () => void | Promise<unknown>;
}
export const DamageLabourGrid: FC<DamageLabourGridProps> = ({
  title,
  canEditFr,
  canEditParts,
  inspectionId,
  damageLine,
  damageLines,
  preferredDamageLineIds,
  damageLinesLoading,
  onSubmit,
}) => {
  const { t } = useTranslate();
  const { message, modal } = App.useApp();

  const [editingRows, setEditingRows] = useState<DamageActionData[]>([]);
  const [damageLabour, setDamageLabour] = useState<DamageActionData[]>([]);
  const { snapshot, refreshing, refreshDamageLines } =
    useRefreshDamageLines<DamageActionData>();

  const { data: labourTypes, isPending: labourLoading } =
    useInspectionSearchLabour(inspectionId);

  const list: DamageActionData[] = (
    damageLine
      ? damageLine.dataSource.map(
          (o, idx) =>
            ({
              id: idx,
              actionId: o.id,
              referenceId: o.referenceId,
              code: o.labourType,
              description: o.comment || o.labourType,
              quantity: o.quantity,
              price: o.price,
              damageLineIds: [damageLine.damageLineId],
            }) as DamageActionData,
        )
      : []
  ).concat(damageLabour);

  const [error, setError] = useState<string>();

  const {
    mutateAsync: addDamageLabourAsync,
    isPending: addDamageActionsLoading,
  } = useAddInspectionsVehicleDamageActions();

  const { mutateAsync: deleteDamageActionAsync } =
    useDeleteInspectionsVehicleDamageAction();

  const getDefaultFlatRate = useCallback(
    (id: number) =>
      ({
        id: id,
        referenceId: 0,
        code: '',
        description: '',
        quantity: 1,
        price: 0,
        damageLineIds: damageLine?.damageLineId
          ? [damageLine.damageLineId]
          : preferredDamageLineIds || [],
      }) as DamageActionData,
    [damageLine?.damageLineId, preferredDamageLineIds],
  );

  const resetRecords = useCallback(() => {
    setDamageLabour(damageLine?.damageLineId ? [] : [getDefaultFlatRate(0)]);
    setEditingRows([]);
  }, [damageLine?.damageLineId, getDefaultFlatRate]);

  useEffect(() => {
    resetRecords();
  }, [resetRecords]);

  const onAdd = useCallback(
    (data?: DamageActionData) => {
      const copyList = [...damageLabour];
      const nextId =
        copyList.length > 0
          ? Math.max(...copyList.map((o) => o.id)) + 1
          : damageLine?.dataSource.length || 0;
      if (data) data.id = nextId;
      copyList.push(data || getDefaultFlatRate(nextId));
      setDamageLabour(copyList);
    },
    [damageLabour, damageLine?.dataSource.length, getDefaultFlatRate],
  );
  const frAddRef = useInlineAddShortcut(
    'fr',
    isAddFrShortcut,
    () => onAdd(),
    !!canEditFr,
  );

  const onDelete = (id: number, exist: boolean) => {
    if (exist) {
      modal.confirm({
        title: 'Confirm',
        content: t('deleteRecordQuestion'),
        okText: t('yes'),
        onOk: async () => {
          const res = await deleteDamageActionAsync(id);
          if (res.message) {
            message.error(res.message.Message);
          } else {
            onSubmit();
          }
        },
        onCancel: () => {},
      });
    } else {
      const copyList = [...damageLabour].filter((o) => o.id !== id);
      setDamageLabour(copyList);
    }
  };

  const onChangeValue = (
    id: number,
    field: keyof DamageActionData,
    value: any,
  ) => {
    setError(undefined);
    if (editingRows.some((row) => row.id === id && row.actionId)) {
      setEditingRows((current) =>
        patchEditingRow(current, id, { [field]: value }),
      );
      return;
    }
    const copyList = [...damageLabour];
    const part: any = copyList.find((o) => o.id === id);
    if (part) {
      part[field] = value;
      setDamageLabour(copyList);
    }
  };

  const onChangeLabour = (id: number, selectedOption: LabourCode) => {
    if (editingRows.some((row) => row.id === id && row.actionId)) {
      setEditingRows((current) =>
        current.map((row) =>
          row.id === id && row.actionId
            ? {
                ...row,
                referenceId: selectedOption.id,
                code: selectedOption.description,
                description: row.description?.trim()
                  ? row.description
                  : selectedOption.description,
                quantity: 1,
                price: selectedOption.cost,
              }
            : row,
        ),
      );
      return;
    }
    const copyList = [...damageLabour];
    const item = copyList.find((o) => o.id === id);
    if (item) {
      item.referenceId = selectedOption.id;
      item.code = selectedOption.description;
      if (!item.description || item.description.trim() === '') {
        item.description = selectedOption.description;
      }
      item.quantity = 1;
      item.price = selectedOption.cost;
      setDamageLabour(copyList);
    }
  };

  const onSave = async () => {
    if (damageLabour.some((o) => !o.referenceId)) {
      setError('Some of the code not choosed');
      return;
    }
    if (
      damageLabour.some((o) => o.quantity < 0) ||
      editingRows.some((o) => o.quantity < 0)
    ) {
      setError('The quantity must be a non-negative number');
      return;
    }
    if (
      damageLabour.some((o) => o.price < 0) ||
      editingRows.some((o) => o.price < 0)
    ) {
      setError('The cost must be a non-negative number');
      return;
    }
    if (damageLabour.some((o) => o.damageLineIds.length == 0)) {
      setError('Please choose the damage lines');
      return;
    }
    if (editingRows.some((o) => !o.referenceId)) {
      setError('Please choose the labour type');
      return;
    }

    const res = await addDamageLabourAsync({
      inspectionId,
      type: 'Labour',
      items: [
        ...editingRows.map((row) => ({
          id: row.actionId,
          referenceId: row.referenceId,
          description: row.description,
          quantity: row.quantity,
          price: row.price,
          damageLineIds: row.damageLineIds,
        })),
        ...damageLabour.map((row) => ({
          referenceId: row.referenceId,
          description: row.description,
          quantity: row.quantity,
          price: row.price,
          damageLineIds: row.damageLineIds,
        })),
      ],
    });
    if (!res.success) {
      setError(res.message.Message);
      return;
    }

    await refreshDamageLines(
      withEditingRows(list, editingRows, 'actionId'),
      onSubmit,
      resetRecords,
    );
  };

  const readOnly = (record: DamageActionData) =>
    !canEditFr ||
    (!!record.actionId &&
      !isExistingRowEditing(record, editingRows, 'actionId'));
  const tableRows = withEditingRows(snapshot ?? list, editingRows, 'actionId');

  const columns: ColumnsType<DamageActionData> = [
    {
      title: t('labourType'),
      width: 150,
      render: (_, record) =>
        readOnly(record) ? (
          record.code
        ) : (
          <Select
            className="w-100"
            value={record.referenceId}
            loading={labourLoading}
            onChange={(e) => {
              const selectedOption = labourTypes?.find((o) => o.id === +e);
              if (selectedOption) {
                onChangeLabour(record.id, selectedOption);
              }
            }}
            options={labourTypes?.map((o) => ({
              label: o.description,
              value: o.id,
            }))}
          />
        ),
    },
    {
      title: 'Minutes',
      width: 100,
      render: (_, record) =>
        readOnly(record) ? (
          record.quantity
        ) : (
          <InputNumber
            value={record.quantity}
            step={1}
            onChange={(e) => onChangeValue(record.id, 'quantity', e || 1)}
          />
        ),
    },
    {
      title: t('price'),
      width: 100,
      render: (_, record) =>
        readOnly(record) ? (
          record.price ? (
            currencyFormatted(record.price) + '/hour'
          ) : null
        ) : (
          <InputNumber
            prefix="£"
            suffix="/hour"
            value={record.price}
            onChange={(e) => onChangeValue(record.id, 'price', e || 0)}
          />
        ),
    },
    {
      title: t('lineCost'),
      width: 100,
      render: (_, record) =>
        currencyFormatted((record.price * record.quantity) / 60),
    },
    ...(damageLine
      ? []
      : [
          {
            title: 'Damages',
            width: 300,
            render: (_: any, record: DamageActionData) => (
              <Select
                className="w-100"
                showSearch
                allowClear
                mode="multiple"
                options={getDamageLineOptions(
                  damageLines || [],
                  record.code,
                  'Labour',
                )}
                value={record.damageLineIds}
                onChange={(value) => {
                  onChangeValue(record.id, 'damageLineIds', value || []);
                }}
              />
            ),
          },
        ]),
    ...(!canEditFr
      ? []
      : [
          {
            dataIndex: 'id',
            width: 88,
            render: (_: any, record: DamageActionData) => (
              <div className="d-flex justify-content-center gap-3">
                {record.actionId ? (
                  isExistingRowEditing(record, editingRows, 'actionId') ? (
                    <StyledLink
                      onClick={() => {
                        setError(undefined);
                        setEditingRows((current) =>
                          removeEditingRow(
                            current,
                            record.actionId,
                            'actionId',
                          ),
                        );
                      }}
                    >
                      <Icon icon={faXmark} />
                    </StyledLink>
                  ) : (
                    <>
                      <StyledLink
                        onClick={() =>
                          setEditingRows((current) =>
                            upsertEditingRow(current, record, 'actionId'),
                          )
                        }
                      >
                        <Icon icon={faEdit} />
                      </StyledLink>
                      <StyledLink
                        onClick={() => onDelete(record.actionId || 0, true)}
                      >
                        <Icon icon={faTrash} />
                      </StyledLink>
                    </>
                  )
                ) : (
                  <Button
                    size="small"
                    shape="circle"
                    color="danger"
                    variant="outlined"
                    onClick={() => onDelete(record.id, false)}
                  >
                    <Icon icon={faMinus} />
                  </Button>
                )}
              </div>
            ),
          },
        ]),
  ];

  return (
    <div ref={frAddRef} data-inline-add="fr">
      <Spin
        spinning={addDamageActionsLoading || refreshing || !!damageLinesLoading}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="m-0 text-uppercase">Repair Operations ({title})</h6>
          {canEditFr ? (
            <div>
              <Button
                className="mx-3"
                title={addFrShortcutHint()}
                shape="circle"
                onClick={() => onAdd()}
              >
                <Icon icon={faAdd} />
              </Button>
              <Button
                type="primary"
                disabled={damageLabour.length === 0 && editingRows.length === 0}
                onClick={onSave}
              >
                {t('save')}
              </Button>
            </div>
          ) : null}
        </div>

        <Table
          columns={columns}
          dataSource={tableRows}
          rowKey="id"
          expandable={
            damageLine
              ? {
                  rowExpandable: (data) => (data.actionId ? true : false),
                  expandedRowRender: (data) => (
                    <Card>
                      <DamageSupplierPartsGrid
                        title={t('parts')}
                        disabled={!canEditParts}
                        inspectionId={inspectionId}
                        damageActionId={data.actionId!}
                        damagePartsData={getActionParts(
                          damageLine?.dataSource,
                          data.actionId,
                        )}
                        damageLinesLoading={damageLinesLoading}
                        onSubmit={onSubmit}
                      />
                    </Card>
                  ),
                }
              : undefined
          }
          pagination={false}
        />

        {error ? <Alert message={error} type="error" className="mt-3" /> : null}
      </Spin>
    </div>
  );
};
